import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Shield, Filter, Star, Bell, HelpCircle, SquareUserRound, Menu, X } from 'lucide-react';
import SideMenu from '../SideMenu/SideMenu';
import Filters from '../Filters/Filters';
import ProfileModal from '../Profile/Profile';
import HelpModal from '../Modals/HelpModal';
import { locationTypes, locationNames, getStateAbbreviation } from '../../constants/locations';
import './Header.css';

const Header = ({ 
  isPinned, 
  setIsPinned, 
  isFilterOpen, 
  setIsFilterOpen, 
  isMobile,
  selectedLocation,
  onLocationSelect,
  selectedCrime,
  setSelectedCrime,
  selectedDateRange,
  setSelectedDateRange,
  selectedSource,
  setSelectedSource,
  availableYears,
  onDefaultViewChange,
  onSaveSearch,
  onOpenSavedSearches,
  unreadCount,
  onOpenAlerts,
  userPlan,
  mostRecentYear,
  openProfileToSubscription,
  liveSearchesUsed,
  liveExportsUsed,
  livePrintsUsed,
  liveSearchHistory,
  onDefaultLocationChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isNavigatingWithKeyboard, setIsNavigatingWithKeyboard] = useState(false);
  const [isTabletLandscape, setIsTabletLandscape] = useState(window.innerWidth >= 1024 && window.innerWidth <= 1300);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileInitialSection, setProfileInitialSection] = useState('account');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const formatLocationName = (locationInfo) => {
    if (!locationInfo?.name) return 'Select Location';
    
    if (locationInfo.type === 'county' && locationInfo.name.includes(', ')) {
      const parts = locationInfo.name.split(', ');
      const county = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${county}, ${stateAbbr}`;
    }
    
    if (locationInfo.type === 'city' && locationInfo.name.includes(', ')) {
      const parts = locationInfo.name.split(', ');
      const city = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${city}, ${stateAbbr}`;
    }
    
    return locationInfo.name;
  };

  const getFlattenedResults = useCallback(() => {
    const results = [];
    [...locationTypes].reverse().forEach(locationType => {
      const typeResults = searchResults.filter(result => result.type === locationType.value);
      results.push(...typeResults);
    });
    return results;
  }, [searchResults]);

  useEffect(() => {
    if (selectedLocation?.info) {
      setInputValue(formatLocationName(selectedLocation.info));
    }
  }, [selectedLocation?.info]);

  useEffect(() => {
    if (openProfileToSubscription) {
      setProfileInitialSection('subscription');
      setIsProfileOpen(true);
    }
  }, [openProfileToSubscription]);

  useEffect(() => {
    if (isNavigatingWithKeyboard && highlightedIndex >= 0) {
      const flatResults = getFlattenedResults();
      if (flatResults[highlightedIndex]) {
        const originalSearchValue = searchQuery || inputValue;
        setInputValue(formatLocationName(flatResults[highlightedIndex]));
        setSearchQuery(originalSearchValue);
      }
    }
  }, [highlightedIndex, isNavigatingWithKeyboard, getFlattenedResults, inputValue, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isModalClick = event.target.closest('.modal-overlay') || 
                          event.target.closest('.city-resolution-modal') ||
                          event.target.closest('.year-selection-modal');
      
      if (searchRef.current && !searchRef.current.contains(event.target) && !isModalClick) {
        setShowDropdown(false);
        setSearchResults([]);
        setSearchQuery('');
        setHighlightedIndex(-1);
        setIsNavigatingWithKeyboard(false);
        setInputValue(selectedLocation?.info ? formatLocationName(selectedLocation.info) : '');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedLocation?.info]);

  useEffect(() => {
    const handleResize = () => {
      setIsTabletLandscape(window.innerWidth >= 1024 && window.innerWidth <= 1300);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
    setShowDropdown(false);
    setSearchQuery('');
    if (isFilterOpen && isPinned !== 'pinned') {
      setIsPinned('unpinned');
    }
  };

  const handleKeyDown = (e) => {    
    if (e.key === 'Escape') {
      e.preventDefault();
      setInputValue('');
      setSearchQuery('');
      setSearchResults([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      setIsNavigatingWithKeyboard(false);
      inputRef.current?.focus();
      return;
    }

    if (!showDropdown || searchResults.length === 0) return;

    const flatResults = getFlattenedResults();
    const maxIndex = flatResults.length - 1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        setIsNavigatingWithKeyboard(true);
        const hoveredItems = dropdownRef.current?.querySelectorAll(':hover');
        hoveredItems?.forEach(item => {
          if (item.tagName.toLowerCase() === 'li') {
            item.style.pointerEvents = 'none';
            setTimeout(() => {
              item.style.pointerEvents = 'auto';
            }, 0);
          }
        });
        
        setHighlightedIndex(prev => {
          const nextIndex = e.key === 'ArrowDown'
            ? (prev < maxIndex ? prev + 1 : 0)
            : (prev > 0 ? prev - 1 : maxIndex);
          scrollToHighlighted(nextIndex);
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        const flatResultsEnter = getFlattenedResults();
        
        if (flatResultsEnter.length > 0) {
          if (highlightedIndex === -1) {
            handleLocationSelect(flatResultsEnter[0]);
          } else {
            handleLocationSelect(flatResultsEnter[highlightedIndex]);
          }
          inputRef.current?.blur();
        }
        break;

      default:
        setIsNavigatingWithKeyboard(false);
        break;
    }
  };

  const scrollToHighlighted = (index) => {
    if (dropdownRef.current) {
      const element = dropdownRef.current.querySelector(`[data-index="${index}"]`);
      if (element) {
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setInputValue(query);
    setSearchQuery(query);
    setIsNavigatingWithKeyboard(false);
    
    if (query.length > 0) {
      const lowercaseQuery = query.toLowerCase();
      
      const metroLetters = ['m', 'me', 'met', 'metr', 'metro'];
      const words = lowercaseQuery.split(/\s+/);
      const hasMetroWord = words.some(word => metroLetters.includes(word));
      
      const queryWithoutMetro = hasMetroWord 
        ? lowercaseQuery.replace(/\b(m|me|met|metr|metro)\b/g, '').trim()
        : lowercaseQuery;
  
      const cityResults = processLocationResults(locationNames.City, 'city', lowercaseQuery);
      const countyResults = processLocationResults(locationNames.County, 'county', lowercaseQuery);
      const metroResults = processLocationResults(locationNames.Metro, 'metro', hasMetroWord ? queryWithoutMetro : lowercaseQuery);
      const stateResults = processLocationResults(locationNames.State, 'state', lowercaseQuery);
      const divisionResults = processLocationResults(locationNames.Division, 'division', lowercaseQuery);
      const regionResults = processLocationResults(locationNames.Region, 'region', lowercaseQuery);
      const nationResults = processLocationResults(locationNames.Nation, 'nation', lowercaseQuery);
      
      const results = [
        ...cityResults,
        ...countyResults,
        ...metroResults,
        ...stateResults,
        ...divisionResults,
        ...regionResults,
        ...nationResults
      ];
      
      setSearchResults(results);
      setShowDropdown(results.length > 0); 
      setHighlightedIndex(-1);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };
  
  const normalizeName = (name) => {
    return name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/-/g, ' ')
      .replace(/[.(),\-']/g, '') 
      .replace(/\s+/g, ' ')
      .trim();
  };

  const processLocationResults = (locations, type, query) => {
    const normalizedQuery = normalizeName(query);
    const trimmedQuery = query.trim();
    
    if (normalizedQuery.length <= 2) {
      const matches = locations
        .filter(name => normalizeName(name).includes(normalizedQuery))
        .map(name => ({ 
          name, 
          type, 
          isExactMatch: normalizeName(name).startsWith(normalizedQuery) 
        }))
        .sort((a, b) => {
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          return 0;
        });
      
      return matches.slice(0, 5);
    }
    
    if ((type === 'county' || type === 'city') && normalizedQuery.length > 0) {
      const startsWithMatches = [];
      const containsMatches = [];
      
      const hasSpace = trimmedQuery.includes(' ');
      
      locations.forEach(fullName => {
        if (fullName.includes(', ')) {
          const [localName, stateName] = fullName.split(', ');
          const normalizedLocalName = normalizeName(localName);
          const normalizedStateName = normalizeName(stateName);
          const stateAbbr = getStateAbbreviation(stateName);
          const normalizedStateAbbr = normalizeName(stateAbbr);
          
          let matches = false;
          let startsWithCity = false;
          let exactCityMatch = false;
          
          if (hasSpace) {
            const lastSpaceIndex = trimmedQuery.lastIndexOf(' ');
            const cityPart = trimmedQuery.substring(0, lastSpaceIndex).trim();
            const statePart = trimmedQuery.substring(lastSpaceIndex + 1).trim();
            const normalizedCityPart = normalizeName(cityPart);
            const normalizedStatePart = normalizeName(statePart);
            
            const words = trimmedQuery.split(/\s+/);
            let altCityPart = '';
            let altStatePart = '';
            if (words.length >= 3) {
              altCityPart = words.slice(0, -2).join(' ');
              altStatePart = words.slice(-2).join(' ');
            }
            const normalizedAltCityPart = normalizeName(altCityPart);
            const normalizedAltStatePart = normalizeName(altStatePart);
            
            const exactMatch = normalizedLocalName.includes(normalizeName(trimmedQuery));
            
            const cityAndStateMatch1 = statePart && normalizedLocalName.includes(normalizedCityPart) && 
                                      (normalizedStateName.startsWith(normalizedStatePart) || 
                                       normalizedStateAbbr.startsWith(normalizedStatePart));
            
            const cityAndStateMatch2 = altCityPart && normalizedLocalName.includes(normalizedAltCityPart) && 
                                      (normalizedStateName.startsWith(normalizedAltStatePart) || 
                                       normalizedStateAbbr.startsWith(normalizedAltStatePart));
            
            if (exactMatch || cityAndStateMatch1 || cityAndStateMatch2) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizeName(trimmedQuery)) || 
                             normalizedLocalName.startsWith(normalizedCityPart) ||
                             (altCityPart && normalizedLocalName.startsWith(normalizedAltCityPart));
              exactCityMatch = normalizedLocalName === normalizeName(trimmedQuery) || 
                             normalizedLocalName === normalizedCityPart ||
                             (altCityPart && normalizedLocalName === normalizedAltCityPart);
            }
          } else {
            if (normalizedLocalName.includes(normalizedQuery) || 
                normalizedStateName.includes(normalizedQuery) || 
                normalizedStateAbbr.includes(normalizedQuery)) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizedQuery);
              exactCityMatch = normalizedLocalName.startsWith(normalizedQuery);
            }
          }
          
          if (matches) {
            const matchData = {
              name: fullName,
              localName,
              normalizedLocalName,
              type: type,
              isExactMatch: exactCityMatch,
              matchType: startsWithCity ? 'starts_with' : 'contains'
            };
            
            if (startsWithCity) {
              startsWithMatches.push(matchData);
            } else {
              containsMatches.push(matchData);
            }
          }
        }
      });
      
      const allMatches = [...startsWithMatches, ...containsMatches];
      
      const sortedMatches = allMatches
        .sort((a, b) => {
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          
          if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
          if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
          
          return a.localName.localeCompare(b.localName);
        });
      
      const exactMatches = sortedMatches.filter(m => m.isExactMatch);
      const exactMatchCount = exactMatches.length;
      
      if (exactMatchCount >= 5) {
        return exactMatches;
      }
      
      return sortedMatches.slice(0, 5);
    }
    
    const startMatches = locations
      .filter(name => normalizeName(name).startsWith(normalizedQuery))
      .map(name => ({ 
        name, 
        type, 
        isExactMatch: !trimmedQuery.includes(' ') && normalizeName(name).startsWith(normalizedQuery),
        matchType: 'starts_with'
      }));
  
    const containsMatches = locations
      .filter(name => {
        const normalizedName = normalizeName(name);
        return normalizedName.includes(normalizedQuery) && !normalizedName.startsWith(normalizedQuery);
      })
      .map(name => ({ 
        name, 
        type, 
        isExactMatch: false,
        matchType: 'contains'
      }));
  
    const allMatches = [...startMatches, ...containsMatches]
      .sort((a, b) => {
        if (a.isExactMatch && !b.isExactMatch) return -1;
        if (!a.isExactMatch && b.isExactMatch) return 1;
        
        if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
        if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
        
        return 0;
      });
  
    const exactMatches = allMatches.filter(m => m.isExactMatch);
    const exactMatchCount = exactMatches.length;
    
    if (exactMatchCount >= 5) {
      return exactMatches;
    }
  
    return allMatches.slice(0, 5);
  };

  const handleLocationSelect = (selection) => {
    if (!selection) return;
    
    setSearchQuery('');
    setInputValue(formatLocationName(selection));
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setIsNavigatingWithKeyboard(false);
    
    onLocationSelect({
      ...selection,
      info: {
        ...selection.info,
        fromHeader: true
      }
    });
  };

  const getDisplayName = (result) => {
    if (result.type === 'county' && result.name.includes(', ')) {
      const parts = result.name.split(', ');
      const county = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${county}, ${stateAbbr}`;
    }
    if (result.type === 'city' && result.name.includes(', ')) {
      const parts = result.name.split(', ');
      const city = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${city}, ${stateAbbr}`;
    }
    return result.name;
  };

  return (
    <>
      <header className="header">
        <div className="header-content">
          <div className={`search-section ${isTabletLandscape ? 'tablet-landscape' : ''}`}>
            <button 
              className="menu-button"
              onClick={() => setIsSideMenuOpen(true)}
            >
              <Menu size={28} />
            </button>
            <div className="search-container" ref={searchRef}>
              <Search className="search-icon" />
              <input
                ref={inputRef}
                type="text"
                className="search-input"
                placeholder="Enter city, county or state"
                value={inputValue}
                onChange={handleSearch}
                onKeyDown={handleKeyDown}
                onFocus={(e) => {
                  if (inputValue && inputValue !== formatLocationName(selectedLocation?.info)) {
                    setShowDropdown(true);
                  }
                }}
              />
              {inputValue && (
                <button 
                  className="clear-search"
                  onClick={(e) => {
                    e.preventDefault();
                    setInputValue('');
                    setSearchQuery('');
                    setSearchResults([]);
                    setShowDropdown(false);
                    setHighlightedIndex(-1);
                    setIsNavigatingWithKeyboard(false);
                    inputRef.current?.focus();
                  }}
                >
                  <X size={16} />
                </button>
              )}
              <button className="filter-button" onClick={handleFilterToggle}>
                <Filter size={20} />
              </button>
              {showDropdown && searchResults.length > 0 && (
                <ul 
                  className="search-dropdown" 
                  ref={dropdownRef}
                  data-keyboard-nav={isNavigatingWithKeyboard}>
                  {locationTypes.slice().reverse().map(locationType => {
                    const typeResults = searchResults.filter(result => result.type === locationType.value);
                    if (typeResults.length === 0) return null;

                    return (
                      <div key={locationType.value} className="search-dropdown-section">
                        <h6 className="search-dropdown-heading">
                          {locationType.label}
                        </h6>
                        {typeResults.map((result) => {
                          const globalIndex = getFlattenedResults().findIndex(
                            item => item.name === result.name && item.type === result.type
                          );
                          return (
                            <li 
                              key={result.name}
                              data-index={globalIndex}
                              className={globalIndex === highlightedIndex ? 'highlighted' : ''}
                              onClick={() => {
                                handleLocationSelect(result);
                                setShowDropdown(false);
                                setSearchQuery('');
                              }}
                              onMouseEnter={() => {
                                setHighlightedIndex(globalIndex);
                                setIsNavigatingWithKeyboard(false);
                              }}
                            >
                              {getDisplayName(result)}
                            </li>
                          );
                        })}
                      </div>
                    );
                  })}
                </ul>
              )}
            </div>
            {isMobile && (
              <button 
                className="profile-button-mobile"
                onClick={() => setIsProfileOpen(true)}
              >
                <SquareUserRound size={28} />
              </button>
            )}
          </div>

          <div className="logo-section">
            <div className="logo">
              <div className="logo-shield">
                <Shield size={50} />
                <span className="logo-text-k">K</span>
              </div>
              <span>RIME</span>
            </div>
          </div>

          <nav className="desktop-nav">
            <div className="nav-item alerts" onClick={onOpenAlerts}>
              <div className="nav-item-icon-wrap">
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="nav-alert-badge">{unreadCount}</span>
                )}
              </div>
              <span>Alerts</span>
            </div>
            <div className="nav-item saved" onClick={onOpenSavedSearches}>
              <Star size={20} />
              <span>Saved</span>
            </div>
            <div className="nav-item help" onClick={() => setIsHelpOpen(true)}>
              <HelpCircle size={20} />
              <span>Help</span>
            </div>
            <div 
              className="nav-item profile"
              onClick={() => setIsProfileOpen(true)}
            >
              <SquareUserRound size={20} />
              <span>Profile</span>
            </div>
          </nav>
        </div>
      </header>

      <SideMenu 
        isOpen={isSideMenuOpen} 
        onClose={() => setIsSideMenuOpen(false)}
        onOpenProfile={() => { setIsSideMenuOpen(false); setIsProfileOpen(true); }}
        onOpenHelp={() => { setIsSideMenuOpen(false); setIsHelpOpen(true); }}
      />

      <ProfileModal 
        isOpen={isProfileOpen}
        onClose={() => { setIsProfileOpen(false); setProfileInitialSection('account'); }}
        onDefaultViewChange={onDefaultViewChange}
        initialSection={profileInitialSection}
        liveSearchesUsed={liveSearchesUsed}
        liveExportsUsed={liveExportsUsed}
        livePrintsUsed={livePrintsUsed}
        liveSearchHistory={liveSearchHistory}
        onDefaultLocationChange={onDefaultLocationChange}
      />

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />

      {isMobile && (
        <Filters 
          isPinned={isPinned}
          setIsPinned={setIsPinned}
          isFilterOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          isMobileLayout={true}
          selectedLocation={selectedLocation}
          onLocationSelect={onLocationSelect}
          selectedCrime={selectedCrime}
          setSelectedCrime={setSelectedCrime}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          selectedSource={selectedSource}
          setSelectedSource={setSelectedSource}
          availableYears={availableYears}
          onSaveSearch={onSaveSearch}
          userPlan={userPlan}
          mostRecentYear={mostRecentYear}
        />
      )}
    </>
  );
};

export default Header;